import { useEffect, useState } from 'react';
import { type AdminMetrics, getAdminMetrics } from '@/services/api/admin';

export function MetricsPanel() {
  const [data, setData] = useState<AdminMetrics | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getAdminMetrics().then(setData).catch(() => setError(true));
  }, []);

  if (error) return <p className="text-sm text-danger">Failed to load metrics.</p>;
  if (!data) return <p className="text-sm text-ink-muted">Loading…</p>;

  const funnel = data.activation_funnel ?? {};
  const signups = funnel.signup ?? data.total_users;
  const firstDiagram = funnel.first_diagram ?? 0;
  const firstExport = funnel.first_iac_export ?? 0;
  const pctDiagram = signups > 0 ? Math.round((firstDiagram / signups) * 100) : 0;
  const pctExport = signups > 0 ? Math.round((firstExport / signups) * 100) : 0;

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-tiny font-semibold uppercase tracking-[0.14em] text-ink-muted [body.paper_&]:text-charcoal-muted">
          Users
        </h2>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total users" value={data.total_users} />
          <Stat label="Signups (7d)" value={data.signups_last_7d} />
          <Stat label="Signups (30d)" value={data.signups_last_30d} />
          <Stat
            label="Paid users"
            value={(data.users_by_plan.pro ?? 0) + (data.users_by_plan.byo ?? 0)}
          />
        </div>
      </section>

      <section>
        <h2 className="text-tiny font-semibold uppercase tracking-[0.14em] text-ink-muted [body.paper_&]:text-charcoal-muted">
          Plan split
        </h2>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <Stat label="Free" value={data.users_by_plan.free ?? 0} />
          <Stat label="Pro" value={data.users_by_plan.pro ?? 0} />
          <Stat label="BYO" value={data.users_by_plan.byo ?? 0} />
        </div>
      </section>

      <section>
        <h2 className="text-tiny font-semibold uppercase tracking-[0.14em] text-ink-muted [body.paper_&]:text-charcoal-muted">
          Activation funnel
        </h2>
        <div className="mt-3 space-y-2">
          <FunnelRow label="Signed up" value={signups} pct={100} />
          <FunnelRow label="Created first diagram" value={firstDiagram} pct={pctDiagram} />
          <FunnelRow label="Exported first IaC" value={firstExport} pct={pctExport} />
        </div>
        <p className="mt-3 text-micro text-ink-faint [body.paper_&]:text-charcoal-faint">
          Activation is tracked the first time each user reaches that step. Numbers are lifetime
          cumulative, not period counts.
        </p>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-line-subtle bg-clyra-raised p-4 [body.paper_&]:border-stone-subtle [body.paper_&]:bg-paper-raised">
      <p className="text-petit text-ink-muted [body.paper_&]:text-charcoal-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-primary [body.paper_&]:text-charcoal">
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function FunnelRow({ label, value, pct }: { label: string; value: number; pct: number }) {
  return (
    <div className="rounded-lg border border-line-subtle bg-clyra-raised px-4 py-3 [body.paper_&]:border-stone-subtle [body.paper_&]:bg-paper-raised">
      <div className="flex items-center justify-between text-sm">
        <span className="text-ink-primary [body.paper_&]:text-charcoal">{label}</span>
        <span className="tabular-nums text-ink-secondary [body.paper_&]:text-charcoal-soft">
          {value.toLocaleString()} <span className="text-tiny text-ink-muted">· {pct}%</span>
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-clyra-overlay [body.paper_&]:bg-paper-sunken">
        <div
          className="h-full bg-brand-500 [body.paper_&]:bg-teal-500"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}
